import Action from '../../elements/progressBar/progressBarAction';
import Container from '../../atoms/container/progressBarContainer';
import Content from '../../elements/progressBar/progressBarContent';
import ProgressUtils from '../../../../shared/utils/progress';
import { PilotProgressBarChannelProps as Props } from '@shared/types/props/pilot';
import { PlayerState } from '../../../../shared/types/enums/player';
import { getNextEvent } from '../../../../shared/store/thunks/events.thunk';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  mediaSelector,
  setPosition,
} from '../../../../shared/store/slices/media.slice';

const SECONDS_TO_MILLISECONDS = 1000;

function ProgressBarChannel({ event }: Props) {
  const dispatch = useAppDispatch();
  const { position, range, ready, state, timeshift } =
    useAppSelector(mediaSelector);

  const [currentPointer, setCurrentPointer] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [liveProgress, setLiveProgress] = useState(0);
  const [hovering, setHovering] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const eventRef = useRef(event);
  eventRef.current = event;

  const { start = 0, end = 0 } = range.event || eventRef.current?.date || {};

  // GEtters

  const getContentDuration = useCallback(() => {
    const contentDuration = (end - start) / SECONDS_TO_MILLISECONDS;
    return contentDuration;
  }, [start, end]);

  const getContentProgress = useCallback(
    (newProgress?: number, newPointer?: number) => {
      const prunedProgress = Math.min(
        liveProgress,
        newProgress || currentProgress
      );
      const prunedPointer = Math.min(newPointer || position, Date.now());
      return { pointer: prunedPointer, progress: prunedProgress };
    },
    [currentProgress, liveProgress, position]
  );

  // Event handlers

  const onMouseLeave = useCallback(() => {
    setHovering(false);
  }, []);

  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!ready) return;
      if (!timeshift) return;
      if (!start && !end) return;
      if (event.target !== event.currentTarget) return;
      if (
        [
          PlayerState.IDLE,
          PlayerState.LOADED,
          PlayerState.SEEKING,
          PlayerState.STALLED,
        ].includes(state)
      ) {
        return;
      }
      if (containerRef.current) {
        const contentDuration = getContentDuration();
        const width = containerRef.current.offsetWidth;
        const offset = event.nativeEvent.offsetX;
        let progress = (offset / width) * 100;
        let pointer =
          start + contentDuration * SECONDS_TO_MILLISECONDS * (progress / 100);
        ({ progress, pointer } = getContentProgress(progress, pointer));
        setCurrentPointer(start && end && pointer);
        setCurrentProgress(start && end && progress);
        setHovering(true);
      }
      event.stopPropagation();
    },
    [getContentDuration, end, position, range, ready, state, timeshift, start]
  );

  const onPointerDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (hovering && range.seek) {
        const { progress } = getContentProgress();
        const contentDuration = getContentDuration();
        const seekStart = start;
        const seekOffset =
          contentDuration * (progress / 100) * SECONDS_TO_MILLISECONDS;
        const position = seekStart + seekOffset;
        dispatch(setPosition({ position, seek: true }));
      }
      event.stopPropagation();
    },
    [
      getContentDuration,
      getContentProgress,
      currentProgress,
      end,
      hovering,
      liveProgress,
      range,
      start,
    ]
  );

  // Effect hooks

  useEffect(() => {
    if (state === PlayerState.SEEKING) {
      setHovering(false);
    }
  }, [state]);

  useEffect(() => {
    if (!start && !end) {
      setCurrentPointer(0);
      setCurrentProgress(100);
      return;
    }
    if (end && position > end) {
      dispatch(getNextEvent(eventRef.current));
      return;
    }
    if (!hovering) {
      const currentPosition = timeshift ? position || Date.now() : Date.now();
      setCurrentPointer(currentPosition);
      setCurrentProgress(
        ProgressUtils.calculateProgressInTime(currentPosition, start, end)
      );
    }
    setLiveProgress(
      position
        ? ProgressUtils.calculateProgressInTime(Date.now(), start, end)
        : 0
    );
  }, [end, hovering, position, start, timeshift]);

  return (
    <>
      <Action />
      <Container
        onClick={onPointerDown}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        ref={containerRef}
        timeshift={timeshift}
      >
        <Content
          animate={false}
          duration={end}
          live={true}
          liveProgress={liveProgress}
          pointer={currentPointer}
          position={start || Date.now()}
          progress={currentProgress}
        />
      </Container>
    </>
  );
}

export default ProgressBarChannel;
